import {
  Form,
  TagPicker,
  SelectPicker,
  Uploader,
  FormControlProps,
  DatePickerProps,
  DatePicker,
  Input,
  InputNumber,
  Loader,
  InputNumberProps,
  TagInput,
} from "rsuite";
import { FileType } from "rsuite/esm/Uploader/Uploader";
import { forwardRef, ReactNode, useState, useMemo, CSSProperties } from "react";
import { ApiSchemaCompany, PaginatedData } from "../models/api";
import { showToast } from "./toast.component";

type Props = {
  required?: boolean;
  parentClassName?: string;
  fullWidth?: boolean;
  wrapperClass?: string;
  wrapperStyle?: CSSProperties;
} & FormControlProps;

export const TextInput = ({
  children,
  required,
  fullWidth,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children && (
        <>
          {children} : {required && <span className="text-red-300">*</span>}
        </>
      )}
    </Form.ControlLabel>
    <Form.Control {...rest} errorPlacement={"topEnd"} />
  </Form.Group>
);

export const PasswordInput = ({
  children,
  required,
  fullWidth,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children && (
        <>
          {children} : {required && <span className="text-red-300">*</span>}
        </>
      )}
    </Form.ControlLabel>
    <Form.Control type="password" {...rest} errorPlacement={"topEnd"} />
  </Form.Group>
);

export const NumberInput = ({
  children,
  required,
  fullWidth,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props & InputNumberProps) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children && (
        <>
          {children} : {required && <span className="text-red-300">*</span>}
        </>
      )}
    </Form.ControlLabel>
    <Form.Control style={{ width: "100%" }} accepter={InputNumber} {...rest} />
  </Form.Group>
);

export const DateInput = ({
  children,
  required,
  fullWidth,
  time,
  month,
  timeChange,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props &
  DatePickerProps & {
    time?: boolean;
    month?:boolean;
    timeChange?: (time: Date) => void;
  }) => {
    const getFormat=()=>{
     if(time) return "hh:mm aa";
     if(month) return "yyyy-MM";
     return "MM-dd-yyyy";
    }

    return (
    <Form.Group
      className={`${wrapperClass ? wrapperClass : ""} ${
        fullWidth ? "px-1" : "col-md-6 col-12 px-1"
      }`}
      style={wrapperStyle}
    >
    <Form.ControlLabel>
      {children && (
        <>
          {children} : {required && <span className="text-red-300">*</span>}
        </>
      )}
    </Form.ControlLabel>
    <Form.Control
      accepter={DatePicker}
      format={getFormat()}
      ranges={[]}
      style={{ width: "100%" }}
      {...rest}
      placement="autoVerticalStart"
      onChangeCalendarDate={timeChange}
      oneTap
    />
  </Form.Group>
)};

export const TagInputWithIcon = ({
  children,
  required,
  fullWidth,
  parentClassName,
  errorMessage,
  renderEmpty,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props & {
  data: Record<string, any>[];
  errorMessage?: string | boolean;
  renderEmpty?: ReactNode;
}) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children} :{required && <span className="text-red-300">*</span>}
      {errorMessage && (
        <small className="text-red-300 d-block">{errorMessage}</small>
      )}
    </Form.ControlLabel>
    <Form.Control
      className={`${rest.className}`}
      style={{ width: "100%" }}
      accepter={TagPicker}
      renderMenu={(menu: ReactNode) =>
        rest.data.length
          ? menu
          : renderEmpty ?? (
              <Loader content="Loading..." vertical center backdrop />
            )
      }
      placement="autoVerticalStart"
      {...rest}
    />
  </Form.Group>
);

const Textarea = forwardRef<HTMLTextAreaElement>((props, ref) => (
  <Input {...props} as="textarea" ref={ref} />
));

export const TextAreaInput = ({
  children,
  required,
  fullWidth,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children} :{required && <span className="text-red-300">*</span>}
    </Form.ControlLabel>
    <Form.Control accepter={Textarea} rows={5} {...rest} />
  </Form.Group>
);

export const SelectPickerInput = ({
  children,
  required,
  fullWidth,
  renderEmpty,
  disabledItemValues,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props & {
  renderEmpty?: ReactNode;
  data: Record<string, any>[];
  searchable?: boolean;
  disabledItemValues?: string[];
}) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children} :{required && <span className="text-red-300">*</span>}
    </Form.ControlLabel>
    <Form.Control
      className={`${rest.className}`}
      style={{ width: "100%" }}
      accepter={SelectPicker}
      renderMenu={(menu: ReactNode) =>
        rest.data.length
          ? menu
          : renderEmpty ?? (
              <Loader content="Loading..." vertical center backdrop />
            )
      }
      placement="autoVerticalStart"
      disabledItemValues={disabledItemValues}
      {...rest}
    />
  </Form.Group>
);

export const FileInput = ({
  children,
  required,
  fullWidth,
  multiple,
  imageSrc,
  imageSrcApi,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props & {
  listType: "picture" | "text" | "picture-text";
  imageSrc?: string;
  accept?: string;
  multiple?: boolean;
  imageSrcApi?: string;
}) => {
  const [files, setFiles] = useState<FileType[]>([]);
  const hideUploader = useMemo(
    () => (!multiple ? !!files.length : files.length > 9),
    [files.length, multiple]
  );

  return (
    <Form.Group
      className={`${wrapperClass ? wrapperClass : ""} ${
        fullWidth ? "px-1" : "col-md-6 col-12 px-1"
      }`}
      style={wrapperStyle}
    >
      <Form.ControlLabel>
        {children} :{required && <span className="text-red-300">*</span>}
      </Form.ControlLabel>

      <Form.Control
        accepter={Uploader}
        autoUpload={false}
        multiple={false}
        fileList={files}
        onChange={(selectedFiles: FileType[]) => {
          const file =
            selectedFiles.length > 0
              ? selectedFiles[selectedFiles.length - 1].blobFile
              : undefined;

          if (!!file?.size && file?.size > 10485760)
            showToast("File size should be less than 10MB", "error");

          setFiles(
            selectedFiles.filter(
              (selectedFile) =>
                !!selectedFile.blobFile?.size &&
                selectedFile.blobFile?.size < 10485760
            )
          );

          return selectedFiles.filter(
            (selectedFile) =>
              !!selectedFile.blobFile?.size &&
              selectedFile.blobFile?.size < 10485760
          );
        }}
        {...rest}
      >
        {imageSrc ? (
          <img
            style={{
              visibility: hideUploader ? "hidden" : "visible",
              height: hideUploader ? 0 : 60,
            }}
            src={imageSrcApi && imageSrcApi !== "" ? imageSrcApi : imageSrc}
            width={60}
            alt="Upload"
          />
        ) : (
          <div
            style={{
              visibility: hideUploader ? "hidden" : "visible",
              height: hideUploader ? 0 : 25,
              marginTop: 5,
            }}
          >
            Upload
          </div>
        )}
      </Form.Control>
    </Form.Group>
  );
};

export const CompanyPicker = ({
  data,
}: {
  data?: PaginatedData<ApiSchemaCompany>;
}) => {
  const companySelectItems = data
    ? data?.list
        .filter((company) => company.name.toLowerCase() !== "webevv")
        .map((company) => ({
          label: company.displayName ?? company.name,
          value: company.sKey,
        }))
    : [];

  return (
    <SelectPickerInput
      data={companySelectItems}
      name="CompanyId"
      required
      renderEmpty={<p className="text-muted p-3">No Data Available</p>}
    >
      Company
    </SelectPickerInput>
  );
};

export const TagWriteInput = ({
  children,
  required,
  fullWidth,
  wrapperClass,
  wrapperStyle,
  ...rest
}: Props) => (
  <Form.Group
    className={`${wrapperClass ? wrapperClass : ""} ${
      fullWidth ? "px-1" : "col-md-6 col-12 px-1"
    }`}
    style={wrapperStyle}
  >
    <Form.ControlLabel>
      {children} :{required && <span className="text-red-300">*</span>}
    </Form.ControlLabel>
    <Form.Control
      className={`${rest.className}`}
      style={{ width: "100%" }}
      accepter={TagInput}
      trigger={["Enter", "Comma"]}
      placeholder="Press Enter/Comma after each entry"
      {...rest}
    />
  </Form.Group>
);
